import { useStore } from 'vuex'
import { useInboxFiltersStore } from '@app/application/communication/screens/inbox/store/inbox-filters'
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { keyBy, uniqBy } from 'lodash'
import {
  combineNotesAndTodos,
  COMPANY_CATEGORY,
  filterRemarks,
  groupCommunicationsByContext,
} from '@app/application/communication/screens/inbox/store/utils'
import * as mutationTypes from '@app/application/communication/screens/inbox/store/mutation-types'
import { SfLog } from '@app/adapters/logger'
import { NoteTypeOption } from '@app/application/communication/screens/inbox/store/constants'

export default function useGroupedFilteredCommunications() {
  const store = useStore()
  const inboxFiltersStore = useInboxFiltersStore()

  const { noteType, tags, authors, searchTerm } = storeToRefs(inboxFiltersStore)

  const filteredNotes = computed(() => {
    if (
      !(
        noteType.value === null ||
        noteType.value === NoteTypeOption.NOTE ||
        noteType.value === NoteTypeOption.MENTIONS
      )
    ) {
      return {}
    }
    const notes = Object.values(store.getters.notes)
    return keyBy(
      filterRemarks(
        notes,
        searchTerm.value.toLowerCase(),
        noteType.value,
        tags.value,
        authors.value,
        store.getters.currentUser,
        store.getters.commentById,
        store.getters.userById
      ),
      'id'
    )
  })

  const filteredTodos = computed(() => {
    if (
      !(
        noteType.value === null ||
        noteType.value === NoteTypeOption.TODO ||
        noteType.value === NoteTypeOption.OPEN_TODO ||
        noteType.value === NoteTypeOption.MENTIONS
      )
    ) {
      return {}
    }
    return keyBy(
      filterRemarks(
        Object.values(store.getters.todos)
          .concat(Object.values(store.getters.sfAssistantCheckTodos))
          .filter(
            (todo) => noteType.value !== NoteTypeOption.OPEN_TODO || !todo.done
          ),
        searchTerm.value.toLowerCase(),
        noteType.value,
        tags.value,
        authors.value,
        store.getters.currentUser,
        store.getters.commentById,
        store.getters.userById
      ),
      'id'
    )
  })

  function groupedItems(items) {
    return groupCommunicationsByContext(
      items,
      store.state.ledgerId,
      store.getters.contextByLedgerTypeAndId,
      store.state.sections.workflows
    )
  }

  const groupedNotes = computed(() =>
    groupedItems(Object.values(filteredNotes.value))
  )
  const groupedReadNotes = computed(() =>
    groupedItems(
      Object.values(filteredNotes.value).filter((n) => {
        const notification = store.getters.notificationsByRemarkId[n.id]
        return (
          !notification ||
          !store.state.notifications.initialUnreadStateById[notification.id]
        )
      })
    )
  )
  const groupedUnreadNotes = computed(() =>
    groupedItems(
      Object.values(filteredNotes.value).filter((n) => {
        const notification = store.getters.notificationsByRemarkId[n.id]
        return (
          !!notification &&
          !!store.state.notifications.initialUnreadStateById[notification.id]
        )
      })
    )
  )
  const groupedTodos = computed(() =>
    groupedItems(Object.values(filteredTodos.value))
  )
  const groupedReadTodos = computed(() =>
    groupedItems(
      Object.values(filteredTodos.value).filter((t) => {
        const notification = store.getters.notificationsByRemarkId[t.id]
        return (
          !notification ||
          !store.state.notifications.initialUnreadStateById[notification.id]
        )
      })
    )
  )
  const groupedUnreadTodos = computed(() =>
    groupedItems(
      Object.values(filteredTodos.value).filter((t) => {
        const notification = store.getters.notificationsByRemarkId[t.id]
        return (
          !!notification &&
          !!store.state.notifications.initialUnreadStateById[notification.id]
        )
      })
    )
  )

  const groupedCommunication = computed(() =>
    combineNotesAndTodos(groupedNotes.value, groupedTodos.value)
  )
  const groupedRead = computed(() =>
    combineNotesAndTodos(groupedReadNotes.value, groupedReadTodos.value)
  )
  const groupedUnread = computed(() =>
    combineNotesAndTodos(groupedUnreadNotes.value, groupedUnreadTodos.value)
  )

  const filteredUnreadNotificationCount = computed(
    () =>
      uniqBy(
        Object.values(groupedUnread.value).flatMap((category) =>
          Object.values(category).flatMap((item) =>
            item.map((comm) =>
              comm.kind === 'remark-card'
                ? store.getters.notificationsByRemarkId[comm.id]
                : store.getters.notificationsByCheckLinkId[comm.id]
            )
          )
        )
        // We need this extra filter, because we sometimes want to keep read items around in the
        // unread section. The counter should still reflect the actual number of unread items though.
      ).filter((notification) => !notification.read).length
  )

  const filterByContext = computed(
    () =>
      store.state.contextType === 'company_workflow' && !!store.state.contextId
  )

  const workflowToShow = computed(() => {
    if (filterByContext.value) {
      return store.getters.workflowsById[store.state.contextId]?.name
    } else {
      return null
    }
  })

  const sections = computed(() => store.getters.sections)

  function getNotificationForCommsItem(comm) {
    return comm.kind === 'remark-card'
      ? store.getters.notificationsByRemarkId[comm.id]
      : store.getters.notificationsByCheckLinkId[comm.id]
  }

  function sectionHasActualUnread(sectionId) {
    return Object.values(groupedUnread.value[sectionId] || {}).reduce(
      (prev, items) =>
        prev ||
        items.reduce(
          (prev, comm) => prev || !getNotificationForCommsItem(comm).read,
          false
        ),
      false
    )
  }

  const menuSections = computed(() => {
    return sections.value
      .filter((s) => !!groupedCommunication.value[s.categoryId])
      .filter(
        (s) => !filterByContext.value || s.categoryId === workflowToShow.value
      )
      .map((s) => ({
        ...s,
        hasUnread: sectionHasActualUnread(s.categoryId),
      }))
  })

  const clientOverviewHasUnread = computed(() => {
    return sectionHasActualUnread(COMPANY_CATEGORY)
  })

  const sectionItems = computed(() => {
    const result = {}
    for (const section of menuSections.value) {
      result[section.categoryId] = Object.keys(
        groupedCommunication.value[section.categoryId]
      )
        .sort()
        .map((name) => ({
          name,
          hasUnread: (
            groupedUnread.value[section.categoryId]?.[name] || []
          ).reduce(
            (prev, comm) => prev || !getNotificationForCommsItem(comm).read,
            false
          ),
        }))
    }
    return result
  })

  async function markCategoryAsRead(categoryId) {
    store.commit(mutationTypes.SET_MARKING_AS_READ, true)
    const notifications = Object.values(
      groupedUnread.value[categoryId]
    ).flatMap((item) =>
      item.map((comm) =>
        comm.kind === 'remark-card'
          ? store.getters.notificationsByRemarkId[comm.id]
          : store.getters.notificationsByCheckLinkId[comm.id]
      )
    )
    try {
      const promises = []
      for (const notification of notifications) {
        if (!notification.read) {
          promises.push(
            store.dispatch('toggleReadNotification', notification.id)
          )
        } else {
          store.commit(
            mutationTypes.RESET_NOTIFICATION_READ_CACHE_FOR_ID,
            notification.id
          )
        }
      }
      await Promise.all(promises)
      window.eventHub.$emit('resetNotificationCount')
    } catch (error) {
      SfLog.info('Failed to toggle notification as read', {
        extra: {
          ...window.Silverfin.jsonParams,
          error,
        },
      })
    } finally {
      store.commit(mutationTypes.SET_MARKING_AS_READ, false)
    }
  }

  async function markAllAsRead() {
    store.commit(mutationTypes.SET_MARKING_AS_READ, true)
    const notifications = Object.values(groupedUnread.value).flatMap(
      (category) =>
        Object.values(category).flatMap((item) =>
          item.map((comm) =>
            comm.kind === 'remark-card'
              ? store.getters.notificationsByRemarkId[comm.id]
              : store.getters.notificationsByCheckLinkId[comm.id]
          )
        )
    )
    try {
      const promises = []
      for (const notification of uniqBy(notifications, 'id')) {
        if (!notification.read) {
          promises.push(
            store.dispatch('toggleReadNotification', notification.id)
          )
        } else {
          store.commit(
            mutationTypes.RESET_NOTIFICATION_READ_CACHE_FOR_ID,
            notification.id
          )
        }
      }
      await Promise.all(promises)
      window.eventHub.$emit('resetNotificationCount')
    } catch (error) {
      SfLog.info('Failed to toggle notification as read', {
        extra: {
          ...window.Silverfin.jsonParams,
          error,
        },
      })
    } finally {
      store.commit(mutationTypes.SET_MARKING_AS_READ, false)
    }
  }

  return {
    filteredUnreadNotificationCount,
    groupedCommunication,
    groupedRead,
    groupedUnread,
    filterByContext,
    menuSections,
    sectionItems,
    clientOverviewHasUnread,
    markCategoryAsRead,
    markAllAsRead,
  }
}
